<template>
  <mf-loading loader-type="dialog" :boolean-loader="loadingDialog">
    <base-page-layout title="Permissionamento" class="permissionamento">
      <h4 class="username mb-4">
        Você está editando o usuário: <b>{{ user.name }}</b>
      </h4>

      <v-card v-if="!user.multi_user" class="units mb-5">
        <mf-card-container
          title="Lojas de acesso"
          description="Selecione aqui as lojas as quais esse usuário pode acessar. Lojas não selecionadas não estarão disponíveis para esse usuário."
        >
          <div class="mt-4">
            <v-container class="pa-0 mb-5" fluid>
              <v-row dense>
                <div v-for="(unit, index) in selectedUnits" :key="index" class="unit-chip mb-1 mr-2">{{ unit.id_erp }} - {{ unit.name }}</div>
              </v-row>
            </v-container>
            <mf-select-units-dialog
              template-type="button"
              :selected-units.sync="user.units"
              :units="units"
              :tag-list="tagList"
              :group-list="unitsGroups"
              :store-access="storeAccess"
              is-access-toggle
              enable-inactive-units-selection
            />
          </div>
        </mf-card-container>
      </v-card>

      <v-card class="mb-5">
        <mf-card-container
          title="Perfis de acesso"
          description="Selecione aqui quais são os perfis de acesso que o usuário possui. Os perfis de acesso pré-determinam alguns acessos que o usuário terá."
        >
          <div>
            <access-profile
              :profiles.sync="user.profiles"
              :profile-error="false"
              :user.sync="user"
              :user-profile-error="false"
              :user-ecommerce-error="false"
              :ecommerce.sync="user.ecommerce"
              :ecommerce-error="false"
              :isAdminLast="true"
              @change="changeProfileSelection"
            />
          </div>
        </mf-card-container>
      </v-card>

      <v-card class="areas mb-5">
        <mf-card-container
          v-model="search"
          title="Páginas de acesso"
          description="Selecione aqui as páginas que esse usuário pode acessar. Páginas não selecionadas não estarão disponíveis para esse usuário."
          useSearch
          placeholder="Busque uma página"
        >
          <v-row>
            <v-col v-if="hasCrmPermission" cols="6">
              <v-row class="px-3">
                <v-col class="header-treeview" cols="12">
                  <h2>CBM</h2>
                </v-col>
                <div class="ml-4 mb-3 mt-n4">
                  <v-tooltip v-if="$getAllowedModules()['operational_performance']" right max-width="200">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <mf-toggle v-model="user.accessSensitiveDataEmployees" hide-details>
                          <template slot="label">
                            Permitir acesso a dados sensíveis de funcionários <v-icon small class="pl-2 pt-1">info_outline</v-icon>
                          </template>
                        </mf-toggle>
                      </div>
                    </template>
                    <span>
                      Se ativa, essa função permite que este usuário consiga ver dados como Nome e CPF dos seus funcionários. Ele também vai ser capaz de
                      exportar estes e outros dados.
                    </span>
                  </v-tooltip>
                  <v-tooltip right max-width="200">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <mf-toggle v-model="user.accessSensitiveData" hide-details>
                          <template slot="label">
                            Permitir acesso a dados sensíveis de consumidores <v-icon small class="pl-2 pt-1">info_outline</v-icon>
                          </template>
                        </mf-toggle>
                      </div>
                    </template>
                    <span>
                      Se ativa, o usuário poderá visualizar informações sensíveis dos consumidores, como nome e CPF. Também terá permissão para alterar os
                      horários de funcionamento das lojas, que são comunicados diretamente aos consumidores.
                    </span>
                  </v-tooltip>
                  <v-tooltip right max-width="200">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <mf-toggle v-model="user.downloadSensitiveDataPermission" :disabled="!user.accessSensitiveData" hide-details>
                          <template slot="label">
                            Permitir download de dados sensíveis de consumidores <v-icon small class="pl-2 pt-1">info_outline</v-icon>
                          </template>
                        </mf-toggle>
                      </div>
                    </template>
                    <span>
                      Se ativa, o usuário poderá baixar informações sensíveis dos consumidores, como nome e CPF.
                      <b v-if="!user.accessSensitiveData">Essa permissão só será ativada se o usuário tiver permissão para acessar dados sensíveis.</b>
                    </span>
                  </v-tooltip>
                </div>
              </v-row>
              <div class="treeview-container mb-4">
                <v-treeview
                  ref="treeview"
                  v-model="filteredUserPages"
                  :items="items"
                  :search="search"
                  expand-icon="keyboard_arrow_down"
                  selected-color="primary"
                  open-on-click
                  selectable
                >
                  <template #label="{ item }">
                    <v-tooltip v-if="item.disabled" right max-width="226">
                      <template v-slot:activator="{ on }">
                        <div v-on="on">
                          {{ item.name }}
                        </div>
                      </template>
                      <span> Somente a rede matriz tem acesso à esta funcionalidade </span>
                    </v-tooltip>
                    <div v-else>
                      {{ item.name }}
                    </div>
                  </template>
                </v-treeview>
              </div>
            </v-col>
            <v-col v-if="hasEcommercePermission" cols="6">
              <v-row class="px-3">
                <v-col class="header-treeview" cols="12">
                  <h2>E-commerce</h2>
                </v-col>
              </v-row>
              <div class="treeview-container mb-4">
                <v-treeview
                  ref="treeview"
                  v-model="ecommercePermissions"
                  :items="itemsEcommerce"
                  :search="search"
                  expand-icon="keyboard_arrow_down"
                  selected-color="primary"
                  open-on-click
                  selectable
                ></v-treeview>
              </div>
            </v-col>
          </v-row>

          <mf-action-buttons
            :primary-button="{
              text: 'Selecionar todas as áreas',
              action: selectAllAreas,
              isVisible: true,
            }"
            :extra-button="{
              text: 'Limpar seleção',
              action: clearAllAreas,
              isVisible: true,
            }"
          />
        </mf-card-container>
      </v-card>

      <mf-action-buttons
        :primary-button="{
          text: 'Salvar',
          action: submit,
          isVisible: true,
          isLoading: loading,
        }"
        :cancel-button="{
          text: 'Cancelar',
          action: back,
          isVisible: true,
        }"
      />
    </base-page-layout>
  </mf-loading>
</template>

<script>
import { profilesPages } from '../../mixins/user-profiles'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { mapState, mapGetters } from 'vuex'
import { QUERY_GET_USER, MUTATION_UPDATE_USER } from '@/modules/usuarios/graphql'

export default {
  name: 'Permissions',
  components: {
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    AccessProfile: () => import('../components/AccessProfile.vue'),
  },
  mixins: [profilesPages],
  data: () => ({
    loading: false,
    loadingDialog: true,
    hasCrmPermission: false,
    hasEcommercePermission: false,
    search: '',
    userBackup: {},
    pages: [],
    ecommercePermissions: [],
    otherStoresAccounts: [],
    storeAccess: {
      ecommerce: false,
      crm: false,
    },
    user: {
      ecommerce: [],
      profiles: [],
      units: [],
      accessSensitiveData: false,
      accessSensitiveDataEmployees: false,
      downloadSensitiveDataPermission: false,
    },
    selections: [],
  }),
  computed: {
    ...mapState(['franchiseePermissions', 'franchiseePermissionsRoutes']),
    ...mapGetters(['units', 'tagList', 'unitsGroups']),
    selectedUnits() {
      return this.units && this.user.units ? this.units.filter((unit) => this.user.units.includes(unit.id_erp)) : []
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    filteredUserPages: {
      get() {
        if (!this.pages) return []
        if (!this.isFranchisee) return this.pages
        const pages = [...this.pages]
        const franchiseeRoutes = Object.entries(this.franchiseePermissionsRoutes).map(([key, value]) => value)
        return pages.filter((page) => {
          if (franchiseeRoutes.includes(page)) {
            const permissionKey = Object.keys(this.franchiseePermissionsRoutes).find((key) => {
              return this.franchiseePermissionsRoutes[key] === page
            })
            return this.franchiseePermissions[permissionKey] !== false
          } else {
            return true
          }
        })
      },
      set(newValue) {
        this.pages = newValue
      },
    },
    items() {
      const modules = this.$getAllowedModules()

      let homePermissions = [
        {
          id: 'home',
          name: 'Home',
        },
      ]
      let usuariosPermissions = [
        {
          id: 'usuarios',
          name: 'Usuários',
          children: [
            {
              id: 'usuarios/editar',
              name: 'Editar usuário',
            },
            {
              id: 'usuarios/permissionamento',
              name: 'Permissionamento',
            },
          ],
        },
      ]
      const showIncrementalRevenue = modules['offer_management']
      let empresaPermissions = [
        {
          id: 'empresa',
          name: 'Empresa',
          children: [
            {
              id: 'empresa/faturamento',
              name: 'Faturamento',
            },
            ...(showIncrementalRevenue ? [{ id: 'empresa/faturamento_incremental', name: 'Faturamento incremental' }] : []),
            {
              id: 'empresa/desempenho',
              name: 'Desempenho',
            },
            {
              id: 'empresa/decomposicao',
              name: 'Decomposição',
            },
          ],
        },
      ]
      let clubePermissions = [
        {
          id: 'clube',
          name: 'Clube ',
          children: [
            {
              id: 'clube/dashboard',
              name: 'Dashboard',
            },
            {
              id: 'clube/desempenho',
              name: 'Desempenho',
            },
            {
              id: 'clube/comparativo',
              name: 'Comparativo',
            },
            {
              id: 'clube/lojas',
              name: 'Lojas',
            },
            {
              id: 'clube/performance_operacional',
              name: 'Performance operacional',
            },
          ],
        },
      ]

      let mariaPermissions = [
        {
          id: 'maria',
          name: 'mar.ia',
          children: [
            {
              id: 'maria/quando-vou-vender',
              name: 'Previsão de vendas clube',
            },
            {
              id: 'maria/campanha-da-maria',
              name: 'Campanha da mar.ia',
            },
            {
              id: 'maria/produtos-impulsionadores',
              name: 'Produtos impulsionadores',
            },
          ],
        },
      ]

      let clientesPermissions = [
        {
          id: 'clientes',
          name: 'Clientes',
          children: [
            {
              id: 'clientes/segmentacao',
              name: 'Segmentação',
            },
            {
              id: 'clientes/perfil_demografico',
              name: 'Perfil demografico',
            },
            {
              id: 'clientes/mapa',
              name: 'Mapa',
            },
            {
              id: 'clientes/retencao',
              name: 'Retenção',
            },
            {
              id: 'clientes/interesses',
              name: 'Interesses',
              children: [
                {
                  id: 'clientes/interesses/genero',
                  name: 'Interesses por gênero',
                },
                {
                  id: 'clientes/interesses/idade',
                  name: 'Interesses por idade',
                },
                {
                  id: 'clientes/interesses/pessoa_juridica',
                  name: 'Interesses por pessoa jurídica',
                },
              ],
            },
            {
              id: 'clientes/melhores_clientes',
              name: 'Melhores clientes',
            },
            {
              id: 'clientes/busca_cliente',
              name: 'Busca de clientes',
            },
            {
              id: 'clientes/detalhes_cliente',
              name: 'Detalhes de clientes',
            },
            {
              id: 'clientes/gerenciamento',
              name: 'Gerenciamento de clientes',
              children: [
                {
                  id: 'clientes/gerenciamento/cadastros',
                  name: 'Cadastros',
                  children: [
                    {
                      id: 'clientes/gerenciamento/cadastros/visualizar-cadastros',
                      name: 'Visualização de cadastros',
                    },
                    {
                      id: 'clientes/gerenciamento/cadastros/edicao-cadastros',
                      name: 'Edição de cadastros',
                      disabled: this.disabledByFranchise('clientes/gerenciamento/cadastros/edicao-cadastros'),
                    },
                  ],
                },
                {
                  id: 'clientes/gerenciamento/solicitacoes',
                  name: 'Solicitações',
                },
              ],
            },
            {
              id: 'clientes/rfv',
              name: 'Classificação RFV',
            },
            {
              id: 'clientes/consultar-ofertas',
              name: 'Consultar Benefícios',
            },
            {
              id: 'clientes/desbloquear-cashback',
              name: 'Desbloquear Cashback',
            },
          ],
        },
      ]

      let indexInteresses = clientesPermissions[0].children.findIndex((permission) => permission.id === 'clientes/interesses')
      if (indexInteresses !== -1) {
        clientesPermissions[0].children[indexInteresses].children = clientesPermissions[0].children[indexInteresses].children.filter((permission) => {
          switch (permission.id) {
            case 'clientes/interesses/genero':
              return !modules['uncategorized_products']
            case 'clientes/interesses/pessoa_juridica':
              return !modules['uncategorized_products']
            case 'clientes/interesses/idade':
              return !modules['uncategorized_products']
            default:
              return true
          }
        })
      }

      let indexGerenciamento = clientesPermissions[0].children.findIndex((permission) => permission.id === 'clientes/gerenciamento')
      if (indexGerenciamento !== -1) {
        clientesPermissions[0].children[indexGerenciamento].children = clientesPermissions[0].children[indexGerenciamento].children.filter((permission) => {
          switch (permission.id) {
            case 'clientes/gerenciamento/solicitacoes':
              return !this.$getLoggedUser().is_franchisee
            default:
              return true
          }
        })
        if (this.$getAllowedModules()['points']) {
          clientesPermissions[0].children[indexGerenciamento].children[0].children.push({
            id: 'clientes/gerenciamento/cadastros/edicao-pontos',
            name: 'Edição de pontos',
            disabled: this.disabledByFranchise('clientes/gerenciamento/cadastros/edicao-pontos'),
          })
        }
      }

      if (this.$getLoggedUser().only_mercapromo) {
        let consultarOfertasIndex = clientesPermissions[0].children.findIndex((permission) => permission.id === 'clientes/consultar-ofertas')
        let desbloquearCashbackIndex = clientesPermissions[0].children.findIndex((permission) => permission.id === 'clientes/desbloquear-cashback')
        clientesPermissions[0].children = [clientesPermissions[0].children[consultarOfertasIndex], clientesPermissions[0].children[desbloquearCashbackIndex]]
      }

      let produtosPermissions = [
        {
          id: 'produtos',
          name: 'Produtos',
          children: [
            {
              id: 'produtos/busca_produto',
              name: 'Busca de produto',
            },
            {
              id: 'produtos/detalhes_produto',
              name: 'Detalhes do produto',
            },
            {
              id: 'produtos/ranking_produtos',
              name: 'Ranking de produtos',
            },
            {
              id: 'produtos/arvore_produtos',
              name: 'Arvore de produtos',
            },
            {
              id: 'produtos/comparativo_produtos',
              name: 'Comparativo de produtos',
            },
            {
              id: 'produtos/gerenciamento_produtos',
              name: 'Gerenciamento de produtos',
            },
            {
              id: 'produtos/faixas_precos',
              name: 'Faixas de preços',
            },
            ...(this.$getAllowedModules()['products_tree']
              ? [
                  {
                    id: 'produtos/comparativo_categorias',
                    name: 'Comparativo de categorias',
                  },
                ]
              : []),
          ],
        },
      ]
      produtosPermissions[0].children = produtosPermissions[0].children.filter((permission) => {
        switch (permission.id) {
          case 'produtos/arvore_produtos':
            return modules['products_tree']
          default:
            return true
        }
      })

      let pedidosPermissions = [
        {
          id: 'pedidos',
          name: 'Pedidos',
          children: [
            {
              id: 'pedidos/controle-pedidos',
              name: 'Controle de pedidos',
            },
            {
              id: 'pedidos/gerenciamento-preco',
              name: 'Gerenciamento de preço',
            },
          ],
        },
      ]

      pedidosPermissions[0].children = pedidosPermissions[0].children.filter((permission) => {
        const modules = this.$getAllowedModules()
        switch (permission.id) {
          case 'pedidos/controle-pedidos':
            return modules['telesales']
          case 'pedidos/gerenciamento-preco':
            return modules['telesales']
          default:
            return true
        }
      })
      pedidosPermissions = modules['telesales'] ? pedidosPermissions : []

      const showPoints = modules['points'] && !this.$getLoggedUser().is_franchisee
      const showChatbot = modules['chatbot']
      const showRaffle = modules['raffle']

      let mobilePermissions = [
        {
          id: 'mobile',
          name: 'Aplicativo',
          children: [
            {
              id: 'mobile/banners',
              name: 'Banners',
              disabled: this.disabledByFranchise('mobile/banners'),
            },
            {
              id: 'mobile/ofertas',
              name: 'Ofertas do aplicativo',
              children: [
                {
                  id: 'mobile/ofertas/produtos',
                  name: 'Produtos',
                  children: [
                    {
                      id: 'mobile/ofertas/produtos/edicao',
                      name: 'Edição das ofertas do aplicativo',
                      disabled: this.disabledByFranchise('mobile/ofertas/produtos'),
                    },
                    ...(this.$getLoggedUser().is_franchisee
                      ? [
                          {
                            id: 'mobile/ofertas/produtos/edicao-lojas-franquia',
                            name: 'Edição de lojas das ofertas da franquia',
                            disabled: this.disabledByFranchise('mobile/ofertas/produtos/edicao-lojas-franquia'),
                          },
                        ]
                      : []),
                  ],
                },
                {
                  id: 'mobile/ofertas/destaques',
                  name: 'Destaques',
                  disabled: this.disabledByFranchise('mobile/ofertas/destaques'),
                },
              ],
            },
            ...(showPoints ? [{ id: 'mobile/pontos', name: 'Pontos' }] : []),
            {
              id: 'mobile/desempenho',
              name: 'Desempenho',
              disabled: this.disabledByFranchise('mobile/desempenho'),
            },
            {
              id: 'mobile/encartes',
              name: 'Encartes',
              disabled: this.disabledByFranchise('mobile/encartes'),
            },
            {
              id: 'mobile/destaques',
              name: 'Destaques',
              disabled: this.disabledByFranchise('mobile/encartes'),
            },
            ...(showChatbot ? [{ id: 'mobile/chatbot', name: 'Chatbot' }] : []),
            ...(showRaffle ? [{ id: 'mobile/sorteio', name: 'Sorteio' }] : []),
          ],
        },
      ]

      if (modules['cashback']) {
        mobilePermissions[0].children.push({
          id: 'mobile/cashback',
          name: 'Cashback',
          children: [
            {
              id: 'mobile/cashback/cashback',
              name: 'Cashback',
            },
            {
              id: 'mobile/cashback/ajustes',
              name: 'Ajustes de cashback',
              disabled: this.disabledByFranchise('mobile/cashback/ajustes'),
            },
            {
              id: 'mobile/cashback/controle-de-cpf',
              name: 'Controle de CPFs',
            },
            ...(modules.jane
              ? [
                  {
                    id: 'mobile/cashback/bloqueios-de-saldos',
                    name: 'Bloqueio de Saldos',
                  },
                ]
              : []),
          ],
        })
      }

      mobilePermissions = modules['mobile'] ? mobilePermissions : []

      let configurationPermissions = [
        {
          id: 'configuracoes',
          name: 'Configurações',
          children: [
            {
              id: 'configuracoes/lojas',
              name: 'Lojas',
            },
            {
              id: 'configuracoes/redes-sociais',
              name: 'Redes Sociais',
            },
            {
              id: 'configuracoes/site-cadastramento-base',
              name: 'Site de cadastramento',
              children: [
                {
                  id: 'configuracoes/site-cadastramento',
                  name: 'Configurações gerais de hotsite',
                },
                {
                  id: 'configuracoes/site-cadastramento/gerenciamento-hotsite',
                  name: 'Gerenciamento de hotsite',
                },
              ],
            },
            {
              id: 'configuracoes/gerais',
              name: 'Gerais',
            },
            {
              id: 'configuracoes/mensagens',
              name: 'Mensagens',
            },
            {
              id: 'configuracoes/permissionamento-filiado',
              name: 'Permissionamento do Filiado',
            },
          ],
        },
      ]

      configurationPermissions[0].children = configurationPermissions[0].children.filter((permission) => {
        switch (permission.id) {
          case 'configuracoes/permissionamento-filiado':
            return this.$getLoggedUser().is_franchise
          default:
            return true
        }
      })
      const showCoupon = modules['discount_coupon']
      let campanhasPermissions = [
        {
          id: 'campanhas',
          name: 'Campanhas',
          children: [
            {
              id: 'campanhas/calendario_promocional',
              name: 'Calendário promocional',
            },
            {
              id: 'campanhas/gestao_ofertas',
              name: 'Gestão de ofertas',
              children: [
                {
                  id: 'campanhas/gestao_ofertas/ofertas',
                  name: 'Gerenciar as ofertas',
                },
              ],
            },
            {
              id: 'campanhas/whatsapp',
              name: 'WhatsApp',
            },
            {
              id: 'campanhas/sms',
              name: 'SMS',
            },
            {
              id: 'campanhas/aplicativo',
              name: 'Aplicativo',
              disabled: this.disabledByFranchise('campanhas/aplicativo'),
            },
            {
              id: 'campanhas/email',
              name: 'E-mail',
            },
            {
              id: 'campanhas/resultados_encarte',
              name: 'Resultados encarte',
            },
            {
              id: 'campanhas/email-v2',
              name: 'E-mail',
            },
            {
              id: 'campanhas/resultados_gerais',
              name: 'Resultados gerais de campanhas',
            },
            ...(showCoupon ? [{ id: 'campanhas/cupom_desconto', name: 'Cupom de desconto' }] : []),
          ],
        },
      ]
      campanhasPermissions[0].children = campanhasPermissions[0].children.filter((permission) => {
        const modules = this.$getAllowedModules()
        switch (permission.id) {
          case 'campanhas/gestao_ofertas':
            return modules['offer_management']
          case 'campanhas/whatsapp':
            return modules['whatsapp']
          case 'campanhas/aplicativo':
            return modules['push']
          case 'campanhas/email-v2':
            return modules['email_v2']
          default:
            return true
        }
      })

      let industriaPermissions = []
      if (this.$getAllowedModules()['industry']) {
        industriaPermissions = [
          {
            id: 'industria',
            name: 'Mercatrade',
            children: [
              {
                id: 'industria/ofertas',
                name: 'Ofertas de trade',
                children: [
                  {
                    id: 'industria/ofertas_trade',
                    name: 'Gerenciar as ofertas',
                  },
                  ...(this.$getLoggedUser().is_franchisee
                    ? [
                        {
                          id: 'industria/ofertas_trade/resultados_franquia',
                          name: 'Ver resultados de ofertas de trade da franquia em suas lojas',
                          disabled: this.disabledByFranchise('industria/ofertas_trade/resultados_franquia'),
                        },
                      ]
                    : []),
                ],
              },
              {
                id: 'industria/sms_trade',
                name: 'SMS de trade',
              },
              {
                id: 'industria/cashback_trade',
                name: 'Cashback de trade',
              },
            ],
          },
        ]
      }
      const hubPermissions = [
        ...(showIncrementalRevenue
          ? [
              {
                id: 'gerenciador-analises',
                name: 'Gerenciador de análises',
              },
            ]
          : []),
      ]

      // if (this.$getAllowedModules()['tribanco_basico']) {
      //   clubePermissions[0].children = clubePermissions[0].children.filter((permission) => {
      //     return permission.id === 'clube/desempenho' || permission.id === 'clube/comparativo'
      //   })

      //   clientesPermissions[0].children = clientesPermissions[0].children.filter((permission) => {
      //     return permission.id === 'clientes/segmentacao' || permission.id === 'clientes/perfil_demografico' || permission.id === 'clientes/melhores_clientes'
      //   })

      //   produtosPermissions[0].children = produtosPermissions[0].children.filter((permission) => permission.id === 'produtos/ranking_produtos')

      //   campanhasPermissions[0].children = campanhasPermissions[0].children.filter((permission) => {
      //     return permission.id === 'campanhas/sms' || permission.id === 'campanhas/email-v2'
      //   })
      //   return [...homePermissions, ...clubePermissions, ...clientesPermissions, ...produtosPermissions, ...usuariosPermissions, ...campanhasPermissions]
      // }

      if (this.$getLoggedUser().only_mercapromo) {
        return [...homePermissions, ...usuariosPermissions, ...industriaPermissions, ...clientesPermissions, ...produtosPermissions]
      } else {
        return [
          ...homePermissions,
          ...usuariosPermissions,
          ...empresaPermissions,
          ...mariaPermissions,
          ...clubePermissions,
          ...clientesPermissions,
          ...produtosPermissions,
          ...pedidosPermissions,
          ...mobilePermissions,
          ...configurationPermissions,
          ...campanhasPermissions,
          ...industriaPermissions,
          ...hubPermissions,
        ]
      }
    },
    itemsEcommerce() {
      return [
        {
          id: 'accounts',
          name: 'Contas',
          children: [
            {
              id: 'endereco',
              name: 'Endereço',
              children: [
                {
                  name: 'Adicionar Endere\u00e7o',
                  id: 'add_endereco',
                },
                {
                  name: 'Editar Endere\u00e7o',
                  id: 'change_endereco',
                  module: 'accounts',
                  model: { id: 'endereco', name: 'Endere\u00e7o' },
                },
                {
                  name: 'Deletar Endere\u00e7o',
                  id: 'delete_endereco',
                  module: 'accounts',
                  model: { id: 'endereco', name: 'Endere\u00e7o' },
                },
                {
                  name: 'Visualizar Endere\u00e7o',
                  id: 'view_endereco',
                  module: 'accounts',
                  model: { id: 'endereco', name: 'Endere\u00e7o' },
                },
              ],
            },
            {
              id: 'notificacaopush',
              name: 'Notificação push',
              children: [
                {
                  name: 'Adicionar Notifica\u00e7\u00e3o push',
                  id: 'add_notificacaopush',
                  module: 'accounts',
                  model: {
                    id: 'notificacaopush',
                    name: 'Notifica\u00e7\u00e3o Push',
                  },
                },
                {
                  name: 'Visualizar Notifica\u00e7\u00e3o push',
                  id: 'view_notificacaopush',
                  module: 'accounts',
                  model: {
                    id: 'notificacaopush',
                    name: 'Notifica\u00e7\u00e3o Push',
                  },
                },
              ],
            },
            {
              id: 'user',
              name: 'Usuário',
              children: [
                {
                  name: 'Adicionar Usu\u00e1rio',
                  id: 'add_user',
                  module: 'accounts',
                  model: { id: 'user', name: 'Usu\u00e1rio' },
                },
                {
                  name: 'Editar Usu\u00e1rio',
                  id: 'change_user',
                  module: 'accounts',
                  model: { id: 'user', name: 'Usu\u00e1rio' },
                },
                {
                  name: 'Visualizar Usu\u00e1rio',
                  id: 'view_user',
                  module: 'accounts',
                  model: { id: 'user', name: 'Usu\u00e1rio' },
                },
              ],
            },
            {
              name: 'Visualizar Dispositivo',
              id: 'view_userdevice',
              module: 'accounts',
              model: { id: 'userdevice', name: 'Dispositivo' },
            },
          ],
        },
        {
          name: 'Visualizar log entry',
          id: 'view_logentry',
          module: 'admin',
          model: { id: 'logentry', name: 'Entrada De Log' },
        },
        {
          name: 'Visualizar busca sem resultado',
          id: 'view_buscasemresultado',
          module: 'busca',
          model: { id: 'buscasemresultado', name: 'Busca' },
        },
        {
          id: 'checkout',
          name: 'Checkout',
          children: [
            {
              id: 'canhoto',
              name: 'Canhoto',
              children: [
                {
                  name: 'Adicionar Canhoto no relat\u00f3rio de pedido',
                  id: 'add_canhotorelatoriopedido',
                  module: 'checkout',
                  model: {
                    id: 'canhotorelatoriopedido',
                    name: 'Canhoto No Relat\u00f3rio De Pedido',
                  },
                },
                {
                  name: 'Editar Canhoto no relat\u00f3rio de pedido',
                  id: 'change_canhotorelatoriopedido',
                  module: 'checkout',
                  model: {
                    id: 'canhotorelatoriopedido',
                    name: 'Canhoto No Relat\u00f3rio De Pedido',
                  },
                },
                {
                  name: 'Deletar Canhoto no relat\u00f3rio de pedido',
                  id: 'delete_canhotorelatoriopedido',
                  module: 'checkout',
                  model: {
                    id: 'canhotorelatoriopedido',
                    name: 'Canhoto No Relat\u00f3rio De Pedido',
                  },
                },
                {
                  name: 'Visualizar Canhoto no relat\u00f3rio de pedido',
                  id: 'view_canhotorelatoriopedido',
                  module: 'checkout',
                  model: {
                    id: 'canhotorelatoriopedido',
                    name: 'Canhoto No Relat\u00f3rio De Pedido',
                  },
                },
              ],
            },
            {
              name: 'Visualizar Carrinho',
              id: 'view_carrinho',
              module: 'checkout',
              model: { id: 'carrinho', name: 'Carrinho' },
            },
            {
              id: 'cupomdesconto',
              name: 'Cupom de desconto',
              children: [
                {
                  name: 'Adicionar cupom de desconto',
                  id: 'add_cupomdesconto',
                  module: 'checkout',
                  model: { id: 'cupomdesconto', name: 'Cupom De Desconto' },
                },
                {
                  name: 'Editar cupom de desconto',
                  id: 'change_cupomdesconto',
                  module: 'checkout',
                  model: { id: 'cupomdesconto', name: 'Cupom De Desconto' },
                },
                {
                  name: 'Deletar cupom de desconto',
                  id: 'delete_cupomdesconto',
                  module: 'checkout',
                  model: { id: 'cupomdesconto', name: 'Cupom De Desconto' },
                },
                {
                  name: 'Visualizar cupom de desconto',
                  id: 'view_cupomdesconto',
                  module: 'checkout',
                  model: { id: 'cupomdesconto', name: 'Cupom De Desconto' },
                },
              ],
            },
            {
              id: 'itemcarrinho',
              name: 'Item do Carrinho',
              children: [
                {
                  name: 'Deletar Item do Carrinho',
                  id: 'delete_itemcarrinho',
                  module: 'checkout',
                  model: { id: 'itemcarrinho', name: 'Item Do Carrinho' },
                },
                {
                  name: 'Visualizar Item do Carrinho',
                  id: 'view_itemcarrinho',
                  module: 'checkout',
                  model: { id: 'itemcarrinho', name: 'Item Do Carrinho' },
                },
              ],
            },
            {
              name: 'Visualizar Item da Lista',
              id: 'view_itemlistacompra',
              module: 'checkout',
              model: { id: 'itemlistacompra', name: 'Item Da Lista' },
            },
            {
              id: 'itempedido',
              name: 'Item do Pedido',
              children: [
                {
                  name: 'Adicionar Item do Pedido',
                  id: 'add_itempedido',
                  module: 'checkout',
                  model: { id: 'itempedido', name: 'Item Do Pedido' },
                },
                {
                  name: 'Editar Item do Pedido',
                  id: 'change_itempedido',
                  module: 'checkout',
                  model: { id: 'itempedido', name: 'Item Do Pedido' },
                },
                {
                  name: 'Deletar Item do Pedido',
                  id: 'delete_itempedido',
                  module: 'checkout',
                  model: { id: 'itempedido', name: 'Item Do Pedido' },
                },
                {
                  name: 'Visualizar Item do Pedido',
                  id: 'view_itempedido',
                  module: 'checkout',
                  model: { id: 'itempedido', name: 'Item Do Pedido' },
                },
              ],
            },
            {
              name: 'Visualizar Lista de compras cliente',
              id: 'view_listacompra',
              module: 'checkout',
              model: { id: 'listacompra', name: 'Lista De Compras Cliente' },
            },
            {
              id: 'motivodevolucao',
              name: 'Motivo devolução',
              children: [
                {
                  name: 'Adicionar Motivo devolu\u00e7\u00e3o',
                  id: 'add_motivodevolucao',
                  module: 'checkout',
                  model: {
                    id: 'motivodevolucao',
                    name: 'Motivo Devolu\u00e7\u00e3o/Cancelamento',
                  },
                },
                {
                  name: 'Editar Motivo devolu\u00e7\u00e3o',
                  id: 'change_motivodevolucao',
                  module: 'checkout',
                  model: {
                    id: 'motivodevolucao',
                    name: 'Motivo Devolu\u00e7\u00e3o/Cancelamento',
                  },
                },
                {
                  name: 'Deletar Motivo devolu\u00e7\u00e3o',
                  id: 'delete_motivodevolucao',
                  module: 'checkout',
                  model: {
                    id: 'motivodevolucao',
                    name: 'Motivo Devolu\u00e7\u00e3o/Cancelamento',
                  },
                },
                {
                  name: 'Visualizar Motivo devolu\u00e7\u00e3o',
                  id: 'view_motivodevolucao',
                  module: 'checkout',
                  model: {
                    id: 'motivodevolucao',
                    name: 'Motivo Devolu\u00e7\u00e3o/Cancelamento',
                  },
                },
              ],
            },
            {
              id: 'pedido',
              name: 'Pedido',
              children: [
                {
                  name: 'Editar Pedido',
                  id: 'change_pedido',
                  module: 'checkout',
                  model: { id: 'pedido', name: 'Pedido' },
                },
                {
                  name: 'Visualizar Pedido',
                  id: 'view_pedido',
                  module: 'checkout',
                  model: { id: 'pedido', name: 'Pedido' },
                },
              ],
            },
            {
              id: 'pedidotelevenda',
              name: 'Pedido Televenda (beta)',
              children: [
                {
                  name: 'Adicionar Pedido Televenda (beta)',
                  id: 'add_pedidotelevenda',
                  module: 'checkout',
                  model: { id: 'pedidotelevenda', name: 'Pedido Televenda (Beta)' },
                },
                {
                  name: 'Editar Pedido Televenda (beta)',
                  id: 'change_pedidotelevenda',
                  module: 'checkout',
                  model: { id: 'pedidotelevenda', name: 'Pedido Televenda (Beta)' },
                },
                {
                  name: 'Visualizar Pedido Televenda (beta)',
                  id: 'view_pedidotelevenda',
                  module: 'checkout',
                  model: { id: 'pedidotelevenda', name: 'Pedido Televenda (Beta)' },
                },
              ],
            },
          ],
        },
        {
          id: 'formularios',
          name: 'Formulários',
          children: [
            {
              id: 'alternativaselect',
              name: 'Alternativa Select',
              children: [
                {
                  name: 'Adicionar Alternativa Select',
                  id: 'add_alternativaselect',
                  module: 'formularios',
                  model: { id: 'alternativaselect', name: 'Alternativa Select' },
                },
                {
                  name: 'Editar Alternativa Select',
                  id: 'change_alternativaselect',
                  module: 'formularios',
                  model: { id: 'alternativaselect', name: 'Alternativa Select' },
                },
                {
                  name: 'Deletar Alternativa Select',
                  id: 'delete_alternativaselect',
                  module: 'formularios',
                  model: { id: 'alternativaselect', name: 'Alternativa Select' },
                },
                {
                  name: 'Visualizar Alternativa Select',
                  id: 'view_alternativaselect',
                  module: 'formularios',
                  model: { id: 'alternativaselect', name: 'Alternativa Select' },
                },
              ],
            },
            {
              id: 'formulario',
              name: 'Formulário',
              children: [
                {
                  name: 'Adicionar Formul\u00e1rio',
                  id: 'add_formulario',
                  module: 'formularios',
                  model: { id: 'formulario', name: 'Formul\u00e1rio' },
                },
                {
                  name: 'Editar Formul\u00e1rio',
                  id: 'change_formulario',
                  module: 'formularios',
                  model: { id: 'formulario', name: 'Formul\u00e1rio' },
                },
                {
                  name: 'Deletar Formul\u00e1rio',
                  id: 'delete_formulario',
                  module: 'formularios',
                  model: { id: 'formulario', name: 'Formul\u00e1rio' },
                },
                {
                  name: 'Visualizar Formul\u00e1rio',
                  id: 'view_formulario',
                  module: 'formularios',
                  model: { id: 'formulario', name: 'Formul\u00e1rio' },
                },
              ],
            },
            {
              name: 'Visualizar Formul\u00e1rio Resposta',
              id: 'view_formularioresposta',
              module: 'formularios',
              model: {
                id: 'formularioresposta',
                name: 'Formul\u00e1rio Resposta',
              },
            },
            {
              id: 'questao',
              name: 'Questão',
              children: [
                {
                  name: 'Adicionar Quest\u00e3o',
                  id: 'add_questao',
                  module: 'formularios',
                  model: { id: 'questao', name: 'Quest\u00e3o' },
                },
                {
                  name: 'Editar Quest\u00e3o',
                  id: 'change_questao',
                  module: 'formularios',
                  model: { id: 'questao', name: 'Quest\u00e3o' },
                },
                {
                  name: 'Deletar Quest\u00e3o',
                  id: 'delete_questao',
                  module: 'formularios',
                  model: { id: 'questao', name: 'Quest\u00e3o' },
                },
                {
                  name: 'Visualizar Quest\u00e3o',
                  id: 'view_questao',
                  module: 'formularios',
                  model: { id: 'questao', name: 'Quest\u00e3o' },
                },
              ],
            },
            {
              name: 'Visualizar Resposta',
              id: 'view_questaoresposta',
              module: 'formularios',
              model: { id: 'questaoresposta', name: 'Resposta' },
            },
          ],
        },
        {
          id: 'gerencial',
          name: 'Gerencial',
          children: [
            {
              id: 'bannerhome',
              name: 'Banner Home',
              children: [
                {
                  name: 'Adicionar Banner Home',
                  id: 'add_bannerhome',
                  module: 'gerencial',
                  model: {
                    id: 'bannerhome',
                    name: 'Banners P\u00e1gina Inicial Topo',
                  },
                },
                {
                  name: 'Editar Banner Home',
                  id: 'change_bannerhome',
                  module: 'gerencial',
                  model: {
                    id: 'bannerhome',
                    name: 'Banners P\u00e1gina Inicial Topo',
                  },
                },
                {
                  name: 'Deletar Banner Home',
                  id: 'delete_bannerhome',
                  module: 'gerencial',
                  model: {
                    id: 'bannerhome',
                    name: 'Banners P\u00e1gina Inicial Topo',
                  },
                },
                {
                  name: 'Visualizar Banner Home',
                  id: 'view_bannerhome',
                  module: 'gerencial',
                  model: {
                    id: 'bannerhome',
                    name: 'Banners P\u00e1gina Inicial Topo',
                  },
                },
              ],
            },
            {
              id: 'bannercabecalho',
              name: 'Banner de Cabeçalho',
              children: [
                {
                  name: 'Adicionar Banner de Cabeçalho',
                  id: 'add_bannercabecalho',
                  module: 'gerencial',
                  model: {
                    id: 'bannercabecalho',
                    name: '-',
                  },
                },
                {
                  name: 'Editar Banner de Cabeçalho',
                  id: 'change_bannercabecalho',
                  module: 'gerencial',
                  model: {
                    id: 'bannercabecalho',
                    name: '-',
                  },
                },
                {
                  name: 'Deletar Banner de Cabeçalho',
                  id: 'delete_bannercabecalho',
                  module: 'gerencial',
                  model: {
                    id: 'bannercabecalho',
                    name: '-',
                  },
                },
                {
                  name: 'Visualizar Banner de Cabeçalho',
                  id: 'view_bannercabecalho',
                  module: 'gerencial',
                  model: {
                    id: 'bannercabecalho',
                    name: '-',
                  },
                },
              ],
            },
            {
              id: 'bannercorpo',
              name: 'Banner de Corpo',
              children: [
                {
                  name: 'Adicionar Banner de Corpo',
                  id: 'add_bannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'bannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Editar Banner de Corpo',
                  id: 'change_bannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'bannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Deletar Banner de Corpo',
                  id: 'delete_bannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'bannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Visualizar Banner de Corpo',
                  id: 'view_bannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'bannercorpo',
                    name: '-',
                  },
                },
              ],
            },
            {
              id: 'posicaobannercorpo',
              name: 'Posição do Banner de Corpo',
              children: [
                {
                  name: 'Adicionar Posição do Banner de Corpo',
                  id: 'add_posicaobannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'posicaobannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Editar Posição do Banner de Corpo',
                  id: 'change_posicaobannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'posicaobannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Deletar Posição do Banner de Corpo',
                  id: 'delete_posicaobannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'posicaobannercorpo',
                    name: '-',
                  },
                },
                {
                  name: 'Visualizar Posição do Banner de Corpo',
                  id: 'view_posicaobannercorpo',
                  module: 'gerencial',
                  model: {
                    id: 'posicaobannercorpo',
                    name: '-',
                  },
                },
              ],
            },
            {
              id: 'bannersdestaqueshome',
              name: 'Banner Destaque Página inicial',
              children: [
                {
                  name: 'Adicionar Banner Destaque P\u00e1gina inicial',
                  id: 'add_bannersdestaqueshome',
                  module: 'gerencial',
                  model: {
                    id: 'bannersdestaqueshome',
                    name: 'Banners P\u00e1gina Inicial Rodap\u00e9',
                  },
                },
                {
                  name: 'Editar Banner Destaque P\u00e1gina inicial',
                  id: 'change_bannersdestaqueshome',
                  module: 'gerencial',
                  model: {
                    id: 'bannersdestaqueshome',
                    name: 'Banners P\u00e1gina Inicial Rodap\u00e9',
                  },
                },
                {
                  name: 'Deletar Banner Destaque P\u00e1gina inicial',
                  id: 'delete_bannersdestaqueshome',
                  module: 'gerencial',
                  model: {
                    id: 'bannersdestaqueshome',
                    name: 'Banners P\u00e1gina Inicial Rodap\u00e9',
                  },
                },
                {
                  name: 'Visualizar Banner Destaque P\u00e1gina inicial',
                  id: 'view_bannersdestaqueshome',
                  module: 'gerencial',
                  model: {
                    id: 'bannersdestaqueshome',
                    name: 'Banners P\u00e1gina Inicial Rodap\u00e9',
                  },
                },
              ],
            },
            {
              id: 'cidade',
              name: 'Cidade',
              children: [
                {
                  name: 'Adicionar cidade',
                  id: 'add_cidade',
                  module: 'gerencial',
                  model: { id: 'cidade', name: 'Cidade' },
                },
                {
                  name: 'Editar cidade',
                  id: 'change_cidade',
                  module: 'gerencial',
                  model: { id: 'cidade', name: 'Cidade' },
                },
                {
                  name: 'Deletar cidade',
                  id: 'delete_cidade',
                  module: 'gerencial',
                  model: { id: 'cidade', name: 'Cidade' },
                },
                {
                  name: 'Visualizar cidade',
                  id: 'view_cidade',
                  module: 'gerencial',
                  model: { id: 'cidade', name: 'Cidade' },
                },
              ],
            },
            {
              id: 'empresa',
              name: 'Empresa',
              children: [
                {
                  name: 'Adicionar empresa',
                  id: 'add_empresa',
                  module: 'gerencial',
                  model: { id: 'empresa', name: 'Empresa' },
                },
                {
                  name: 'Editar empresa',
                  id: 'change_empresa',
                  module: 'gerencial',
                  model: { id: 'empresa', name: 'Empresa' },
                },
                {
                  name: 'Visualizar empresa',
                  id: 'view_empresa',
                  module: 'gerencial',
                  model: { id: 'empresa', name: 'Empresa' },
                },
              ],
            },
            {
              id: 'estado',
              name: 'Estado',
              children: [
                {
                  name: 'Adicionar estado',
                  id: 'add_estado',
                  module: 'gerencial',
                  model: { id: 'estado', name: 'Estado' },
                },
                {
                  name: 'Editar estado',
                  id: 'change_estado',
                  module: 'gerencial',
                  model: { id: 'estado', name: 'Estado' },
                },
                {
                  name: 'Deletar estado',
                  id: 'delete_estado',
                  module: 'gerencial',
                  model: { id: 'estado', name: 'Estado' },
                },
                {
                  name: 'Visualizar estado',
                  id: 'view_estado',
                  module: 'gerencial',
                  model: { id: 'estado', name: 'Estado' },
                },
              ],
            },
            {
              id: 'faixacep',
              name: 'Faixa De Cep',
              children: [
                {
                  name: 'Adicionar faixa de cep',
                  id: 'add_faixacep',
                  module: 'gerencial',
                  model: { id: 'faixacep', name: 'Faixa De Cep' },
                },
                {
                  name: 'Editar faixa de cep',
                  id: 'change_faixacep',
                  module: 'gerencial',
                  model: { id: 'faixacep', name: 'Faixa De Cep' },
                },
                {
                  name: 'Deletar faixa de cep',
                  id: 'delete_faixacep',
                  module: 'gerencial',
                  model: { id: 'faixacep', name: 'Faixa De Cep' },
                },
                {
                  name: 'Visualizar faixa de cep',
                  id: 'view_faixacep',
                  module: 'gerencial',
                  model: { id: 'faixacep', name: 'Faixa De Cep' },
                },
              ],
            },
            {
              id: 'faixadistanciaentrega',
              name: 'Faixa de preço por distância',
              children: [
                {
                  name: 'Adicionar Faixa de pre\u00e7o por dist\u00e2ncia',
                  id: 'add_faixadistanciaentrega',
                  module: 'gerencial',
                  model: {
                    id: 'faixadistanciaentrega',
                    name: 'Faixa De Pre\u00e7o Por Dist\u00e2ncia',
                  },
                },
                {
                  name: 'Editar Faixa de pre\u00e7o por dist\u00e2ncia',
                  id: 'change_faixadistanciaentrega',
                  module: 'gerencial',
                  model: {
                    id: 'faixadistanciaentrega',
                    name: 'Faixa De Pre\u00e7o Por Dist\u00e2ncia',
                  },
                },
                {
                  name: 'Deletar Faixa de pre\u00e7o por dist\u00e2ncia',
                  id: 'delete_faixadistanciaentrega',
                  module: 'gerencial',
                  model: {
                    id: 'faixadistanciaentrega',
                    name: 'Faixa De Pre\u00e7o Por Dist\u00e2ncia',
                  },
                },
                {
                  name: 'Visualizar Faixa de pre\u00e7o por dist\u00e2ncia',
                  id: 'view_faixadistanciaentrega',
                  module: 'gerencial',
                  model: {
                    id: 'faixadistanciaentrega',
                    name: 'Faixa De Pre\u00e7o Por Dist\u00e2ncia',
                  },
                },
              ],
            },
            {
              id: 'feriado',
              name: 'Feriado',
              children: [
                {
                  name: 'Adicionar feriado',
                  id: 'add_feriado',
                  module: 'gerencial',
                  model: { id: 'feriado', name: 'Feriado' },
                },
                {
                  name: 'Editar feriado',
                  id: 'change_feriado',
                  module: 'gerencial',
                  model: { id: 'feriado', name: 'Feriado' },
                },
                {
                  name: 'Deletar feriado',
                  id: 'delete_feriado',
                  module: 'gerencial',
                  model: { id: 'feriado', name: 'Feriado' },
                },
                {
                  name: 'Visualizar feriado',
                  id: 'view_feriado',
                  module: 'gerencial',
                  model: { id: 'feriado', name: 'Feriado' },
                },
              ],
            },
            {
              id: 'formapagamentoentrega',
              name: 'Forma de pagamento na entrega',
              children: [
                {
                  name: 'Adicionar forma de pagamento na entrega',
                  id: 'add_formapagamentoentrega',
                  module: 'gerencial',
                  model: { id: 'formapagamentoentrega', name: 'Forma De Pagamento' },
                },
                {
                  name: 'Editar forma de pagamento na entrega',
                  id: 'change_formapagamentoentrega',
                  module: 'gerencial',
                  model: { id: 'formapagamentoentrega', name: 'Forma De Pagamento' },
                },
                {
                  name: 'Deletar forma de pagamento na entrega',
                  id: 'delete_formapagamentoentrega',
                  module: 'gerencial',
                  model: { id: 'formapagamentoentrega', name: 'Forma De Pagamento' },
                },
                {
                  name: 'Visualizar forma de pagamento na entrega',
                  id: 'view_formapagamentoentrega',
                  module: 'gerencial',
                  model: { id: 'formapagamentoentrega', name: 'Forma De Pagamento' },
                },
              ],
            },
            {
              id: 'formapagamentoonline',
              name: 'Forma de pagamento online',
              children: [
                {
                  name: 'Adicionar forma de pagamento online',
                  id: 'add_formapagamentoonline',
                  module: 'gerencial',
                  model: { id: 'formapagamentoonline', name: 'Gerencial' },
                },
                {
                  name: 'Editar forma de pagamento online',
                  id: 'change_formapagamentoonline',
                  module: 'gerencial',
                  model: { id: 'formapagamentoonline', name: 'Gerencial' },
                },
                {
                  name: 'Deletar forma de pagamento online',
                  id: 'delete_formapagamentoonline',
                  module: 'gerencial',
                  model: { id: 'formapagamentoonline', name: 'Gerencial' },
                },
                {
                  name: 'Visualizar forma de pagamento online',
                  id: 'view_formapagamentoonline',
                  module: 'gerencial',
                  model: { id: 'formapagamentoonline', name: 'Gerencial' },
                },
              ],
            },
            {
              id: 'fretevalorpedido',
              name: 'Frete por valor do pedido',
              children: [
                {
                  name: 'Adicionar frete por valor do pedido',
                  id: 'add_fretevalorpedido',
                  module: 'gerencial',
                  model: {
                    id: 'fretevalorpedido',
                    name: 'Frete Por Valor Do Pedido',
                  },
                },
                {
                  name: 'Editar frete por valor do pedido',
                  id: 'change_fretevalorpedido',
                  module: 'gerencial',
                  model: {
                    id: 'fretevalorpedido',
                    name: 'Frete Por Valor Do Pedido',
                  },
                },
                {
                  name: 'Deletar frete por valor do pedido',
                  id: 'delete_fretevalorpedido',
                  module: 'gerencial',
                  model: {
                    id: 'fretevalorpedido',
                    name: 'Frete Por Valor Do Pedido',
                  },
                },
                {
                  name: 'Visualizar frete por valor do pedido',
                  id: 'view_fretevalorpedido',
                  module: 'gerencial',
                  model: {
                    id: 'fretevalorpedido',
                    name: 'Frete Por Valor Do Pedido',
                  },
                },
              ],
            },
            {
              id: 'geolocalizacao',
              name: 'Geo Localização',
              children: [
                {
                  name: 'Adicionar Geo Localiza\u00e7\u00e3o',
                  id: 'add_geolocalizacao',
                  module: 'gerencial',
                  model: { id: 'geolocalizacao', name: 'Geolocaliza\u00e7\u00e3o' },
                },
                {
                  name: 'Editar Geo Localiza\u00e7\u00e3o',
                  id: 'change_geolocalizacao',
                  module: 'gerencial',
                  model: { id: 'geolocalizacao', name: 'Geolocaliza\u00e7\u00e3o' },
                },
                {
                  name: 'Deletar Geo Localiza\u00e7\u00e3o',
                  id: 'delete_geolocalizacao',
                  module: 'gerencial',
                  model: { id: 'geolocalizacao', name: 'Geolocaliza\u00e7\u00e3o' },
                },
                {
                  name: 'Visualizar Geo Localiza\u00e7\u00e3o',
                  id: 'view_geolocalizacao',
                  module: 'gerencial',
                  model: { id: 'geolocalizacao', name: 'Geolocaliza\u00e7\u00e3o' },
                },
              ],
            },
            {
              id: 'horarioatendimento',
              name: 'Hor\u00e1rio de atendimento',
              children: [
                {
                  name: 'Adicionar Hor\u00e1rio de atendimento',
                  id: 'add_horarioatendimento',
                  module: 'gerencial',
                  model: {
                    id: 'horarioatendimento',
                    name: 'Hor\u00e1rio De Atendimento Delivery',
                  },
                },
                {
                  name: 'Editar Hor\u00e1rio de atendimento',
                  id: 'change_horarioatendimento',
                  module: 'gerencial',
                  model: {
                    id: 'horarioatendimento',
                    name: 'Hor\u00e1rio De Atendimento Delivery',
                  },
                },
                {
                  name: 'Deletar Hor\u00e1rio de atendimento',
                  id: 'delete_horarioatendimento',
                  module: 'gerencial',
                  model: {
                    id: 'horarioatendimento',
                    name: 'Hor\u00e1rio De Atendimento Delivery',
                  },
                },
                {
                  name: 'Visualizar Hor\u00e1rio de atendimento',
                  id: 'view_horarioatendimento',
                  module: 'gerencial',
                  model: {
                    id: 'horarioatendimento',
                    name: 'Hor\u00e1rio De Atendimento Delivery',
                  },
                },
              ],
            },
            {
              id: 'horarioentrega',
              name: 'Hor\u00e1rio de entrega',
              children: [
                {
                  name: 'Adicionar hor\u00e1rio de entrega',
                  id: 'add_horarioentrega',
                  module: 'gerencial',
                  model: { id: 'horarioentrega', name: 'Hor\u00e1rio De Entrega' },
                },
                {
                  name: 'Editar hor\u00e1rio de entrega',
                  id: 'change_horarioentrega',
                  module: 'gerencial',
                  model: { id: 'horarioentrega', name: 'Hor\u00e1rio De Entrega' },
                },
                {
                  name: 'Deletar hor\u00e1rio de entrega',
                  id: 'delete_horarioentrega',
                  module: 'gerencial',
                  model: { id: 'horarioentrega', name: 'Hor\u00e1rio De Entrega' },
                },
                {
                  name: 'Visualizar hor\u00e1rio de entrega',
                  id: 'view_horarioentrega',
                  module: 'gerencial',
                  model: { id: 'horarioentrega', name: 'Hor\u00e1rio De Entrega' },
                },
              ],
            },
            {
              id: 'horarioretirada',
              name: 'Hor\u00e1rio de retirada',
              children: [
                {
                  name: 'Adicionar hor\u00e1rio de retirada',
                  id: 'add_horarioretirada',
                  module: 'gerencial',
                  model: { id: 'horarioretirada', name: 'Hor\u00e1rio De Retirada' },
                },
                {
                  name: 'Editar hor\u00e1rio de retirada',
                  id: 'change_horarioretirada',
                  module: 'gerencial',
                  model: { id: 'horarioretirada', name: 'Hor\u00e1rio De Retirada' },
                },
                {
                  name: 'Deletar hor\u00e1rio de retirada',
                  id: 'delete_horarioretirada',
                  module: 'gerencial',
                  model: { id: 'horarioretirada', name: 'Hor\u00e1rio De Retirada' },
                },
                {
                  name: 'Visualizar hor\u00e1rio de retirada',
                  id: 'view_horarioretirada',
                  module: 'gerencial',
                  model: { id: 'horarioretirada', name: 'Hor\u00e1rio De Retirada' },
                },
              ],
            },
            {
              id: 'integracaoformadepagamentoonline',
              name: 'Forma de Pagamento Online',
              children: [
                {
                  name: 'Adicionar Forma de Pagamento Online',
                  id: 'add_integracaoformadepagamentoonline',
                  module: 'gerencial',
                  model: {
                    id: 'integracaoformadepagamentoonline',
                    name: 'Gerencial',
                  },
                },
                {
                  name: 'Editar Forma de Pagamento Online',
                  id: 'change_integracaoformadepagamentoonline',
                  module: 'gerencial',
                  model: {
                    id: 'integracaoformadepagamentoonline',
                    name: 'Gerencial',
                  },
                },
                {
                  name: 'Deletar Forma de Pagamento Online',
                  id: 'delete_integracaoformadepagamentoonline',
                  module: 'gerencial',
                  model: {
                    id: 'integracaoformadepagamentoonline',
                    name: 'Gerencial',
                  },
                },
                {
                  name: 'Visualizar Forma de Pagamento Online',
                  id: 'view_integracaoformadepagamentoonline',
                  module: 'gerencial',
                  model: {
                    id: 'integracaoformadepagamentoonline',
                    name: 'Gerencial',
                  },
                },
              ],
            },
            {
              id: 'linkdestaque',
              name: 'Link em destaque',
              children: [
                {
                  name: 'Adicionar Link em destaque',
                  id: 'add_linkdestaque',
                  module: 'gerencial',
                  model: { id: 'linkdestaque', name: 'Link Em Destaque' },
                },
                {
                  name: 'Editar Link em destaque',
                  id: 'change_linkdestaque',
                  module: 'gerencial',
                  model: { id: 'linkdestaque', name: 'Link Em Destaque' },
                },
                {
                  name: 'Deletar Link em destaque',
                  id: 'delete_linkdestaque',
                  module: 'gerencial',
                  model: { id: 'linkdestaque', name: 'Link Em Destaque' },
                },
                {
                  name: 'Visualizar Link em destaque',
                  id: 'view_linkdestaque',
                  module: 'gerencial',
                  model: { id: 'linkdestaque', name: 'Link Em Destaque' },
                },
              ],
            },
            {
              id: 'loja',
              name: 'Loja',
              children: [
                {
                  name: 'Adicionar loja',
                  id: 'add_loja',
                  module: 'gerencial',
                  model: { id: 'loja', name: 'Loja' },
                },
                {
                  name: 'Editar loja',
                  id: 'change_loja',
                  module: 'gerencial',
                  model: { id: 'loja', name: 'Loja' },
                },
                {
                  name: 'Visualizar loja',
                  id: 'view_loja',
                  module: 'gerencial',
                  model: { id: 'loja', name: 'Loja' },
                },
              ],
            },
            {
              id: 'notificacaotopo',
              name: 'Notifica\u00e7\u00e3o de topo',
              children: [
                {
                  name: 'Adicionar Notifica\u00e7\u00e3o de topo',
                  id: 'add_notificacaotopo',
                  module: 'gerencial',
                  model: {
                    id: 'notificacaotopo',
                    name: 'Notifica\u00e7\u00e3o De Topo',
                  },
                },
                {
                  name: 'Editar Notifica\u00e7\u00e3o de topo',
                  id: 'change_notificacaotopo',
                  module: 'gerencial',
                  model: {
                    id: 'notificacaotopo',
                    name: 'Notifica\u00e7\u00e3o De Topo',
                  },
                },
                {
                  name: 'Deletar Notifica\u00e7\u00e3o de topo',
                  id: 'delete_notificacaotopo',
                  module: 'gerencial',
                  model: {
                    id: 'notificacaotopo',
                    name: 'Notifica\u00e7\u00e3o De Topo',
                  },
                },
                {
                  name: 'Visualizar Notifica\u00e7\u00e3o de topo',
                  id: 'view_notificacaotopo',
                  module: 'gerencial',
                  model: {
                    id: 'notificacaotopo',
                    name: 'Notifica\u00e7\u00e3o De Topo',
                  },
                },
              ],
            },
            {
              id: 'pagina',
              name: 'P\u00e1gina',
              children: [
                {
                  name: 'Adicionar p\u00e1gina',
                  id: 'add_pagina',
                  module: 'gerencial',
                  model: { id: 'pagina', name: 'Gerencial' },
                },
                {
                  name: 'Editar p\u00e1gina',
                  id: 'change_pagina',
                  module: 'gerencial',
                  model: { id: 'pagina', name: 'Gerencial' },
                },
                {
                  name: 'Deletar p\u00e1gina',
                  id: 'delete_pagina',
                  module: 'gerencial',
                  model: { id: 'pagina', name: 'Gerencial' },
                },
                {
                  name: 'Visualizar p\u00e1gina',
                  id: 'view_pagina',
                  module: 'gerencial',
                  model: { id: 'pagina', name: 'Gerencial' },
                },
              ],
            },
            {
              id: 'parcelamentopagamentocartao',
              name: 'Parcelamento no cart\u00e3o',
              children: [
                {
                  name: 'Adicionar Parcelamento no cart\u00e3o',
                  id: 'add_parcelamentopagamentocartao',
                  module: 'gerencial',
                  model: {
                    id: 'parcelamentopagamentocartao',
                    name: 'Parcelamento No Cart\u00e3o',
                  },
                },
                {
                  name: 'Editar Parcelamento no cart\u00e3o',
                  id: 'change_parcelamentopagamentocartao',
                  module: 'gerencial',
                  model: {
                    id: 'parcelamentopagamentocartao',
                    name: 'Parcelamento No Cart\u00e3o',
                  },
                },
                {
                  name: 'Deletar Parcelamento no cart\u00e3o',
                  id: 'delete_parcelamentopagamentocartao',
                  module: 'gerencial',
                  model: {
                    id: 'parcelamentopagamentocartao',
                    name: 'Parcelamento No Cart\u00e3o',
                  },
                },
                {
                  name: 'Visualizar Parcelamento no cart\u00e3o',
                  id: 'view_parcelamentopagamentocartao',
                  module: 'gerencial',
                  model: {
                    id: 'parcelamentopagamentocartao',
                    name: 'Parcelamento No Cart\u00e3o',
                  },
                },
              ],
            },
            {
              id: 'popuphome',
              name: 'Popup Home',
              children: [
                {
                  name: 'Adicionar Popup Home',
                  id: 'add_popuphome',
                  module: 'gerencial',
                  model: { id: 'popuphome', name: 'Popup Home' },
                },
                {
                  name: 'Editar Popup Home',
                  id: 'change_popuphome',
                  module: 'gerencial',
                  model: { id: 'popuphome', name: 'Popup Home' },
                },
                {
                  name: 'Deletar Popup Home',
                  id: 'delete_popuphome',
                  module: 'gerencial',
                  model: { id: 'popuphome', name: 'Popup Home' },
                },
                {
                  name: 'Visualizar Popup Home',
                  id: 'view_popuphome',
                  module: 'gerencial',
                  model: { id: 'popuphome', name: 'Popup Home' },
                },
              ],
            },
            {
              id: 'regiao',
              name: 'Regi\u00e3o',
              children: [
                {
                  name: 'Adicionar regi\u00e3o',
                  id: 'add_regiao',
                  module: 'gerencial',
                  model: { id: 'regiao', name: 'Regi\u00e3o' },
                },
                {
                  name: 'Editar regi\u00e3o',
                  id: 'change_regiao',
                  module: 'gerencial',
                  model: { id: 'regiao', name: 'Regi\u00e3o' },
                },
                {
                  name: 'Deletar regi\u00e3o',
                  id: 'delete_regiao',
                  module: 'gerencial',
                  model: { id: 'regiao', name: 'Regi\u00e3o' },
                },
                {
                  name: 'Visualizar regi\u00e3o',
                  id: 'view_regiao',
                  module: 'gerencial',
                  model: { id: 'regiao', name: 'Regi\u00e3o' },
                },
              ],
            },
            {
              id: 'taxas',
              name: 'Taxa',
              children: [
                {
                  name: 'Adicionar taxa',
                  id: 'add_taxas',
                  module: 'gerencial',
                  model: { id: 'taxas', name: 'Taxa' },
                },
                {
                  name: 'Editar taxa',
                  id: 'change_taxas',
                  module: 'gerencial',
                  model: { id: 'taxas', name: 'Taxa' },
                },
                {
                  name: 'Deletar taxa',
                  id: 'delete_taxas',
                  module: 'gerencial',
                  model: { id: 'taxas', name: 'Taxa' },
                },
                {
                  name: 'Visualizar taxa',
                  id: 'view_taxas',
                  module: 'gerencial',
                  model: { id: 'taxas', name: 'Taxa' },
                },
              ],
            },
            {
              id: 'tipofrete',
              name: 'Tipo de frete',
              children: [
                {
                  name: 'Adicionar Tipo de frete',
                  id: 'add_tipofrete',
                  module: 'gerencial',
                  model: { id: 'tipofrete', name: 'Tipo De Frete' },
                },
                {
                  name: 'Editar Tipo de frete',
                  id: 'change_tipofrete',
                  module: 'gerencial',
                  model: { id: 'tipofrete', name: 'Tipo De Frete' },
                },
                {
                  name: 'Deletar Tipo de frete',
                  id: 'delete_tipofrete',
                  module: 'gerencial',
                  model: { id: 'tipofrete', name: 'Tipo De Frete' },
                },
              ],
            },
          ],
        },
        {
          id: 'integracao',
          name: 'Integração',
          children: [
            {
              name: 'Visualizar Backup banco de dados',
              id: 'view_backupdb',
              module: 'integracao',
              model: { id: 'backupdb', name: 'Backup Banco De Dados' },
            },
            {
              id: 'categorizarmodelo',
              name: 'Categorizar Modelo',
              children: [
                {
                  name: 'Adicionar Categorizar Modelo',
                  id: 'add_categorizarmodelo',
                  module: 'integracao',
                  model: { id: 'categorizarmodelo', name: 'Categorizar Modelo' },
                },
                {
                  name: 'Editar Categorizar Modelo',
                  id: 'change_categorizarmodelo',
                  module: 'integracao',
                  model: { id: 'categorizarmodelo', name: 'Categorizar Modelo' },
                },
                {
                  name: 'Visualizar Categorizar Modelo',
                  id: 'view_categorizarmodelo',
                  module: 'integracao',
                  model: { id: 'categorizarmodelo', name: 'Categorizar Modelo' },
                },
              ],
            },
            {
              name: 'Visualizar Relat\u00f3rio para Baixar',
              id: 'view_downloadrelatorio',
              module: 'integracao',
              model: {
                id: 'downloadrelatorio',
                name: 'Relat\u00f3rio Para Baixar',
              },
            },
            {
              id: 'uploadjsonprodutos',
              name: 'Importar Json',
              children: [
                {
                  name: 'Deletar Importar Json',
                  id: 'delete_uploadjson',
                  module: 'integracao',
                  model: {
                    id: 'uploadjsonprodutos',
                    name: 'Importar Arquivo Produtos',
                  },
                },
                {
                  name: 'Visualizar Importar Json',
                  id: 'view_uploadjson',
                  module: 'integracao',
                  model: {
                    id: 'uploadjsonprodutos',
                    name: 'Importar Arquivo Produtos',
                  },
                },
              ],
            },
            {
              id: 'uploadjsonprodutosjson',
              name: 'Importar Json Produtos',
              children: [
                {
                  name: 'Deletar Importar Json Produtos',
                  id: 'delete_uploadjsonprodutos',
                  module: 'integracao',
                  model: {
                    id: 'uploadjsonprodutos',
                    name: 'Importar Arquivo Produtos',
                  },
                },
                {
                  name: 'Visualizar Importar Json Produtos',
                  id: 'view_uploadjsonprodutos',
                  module: 'integracao',
                  model: {
                    id: 'uploadjsonprodutos',
                    name: 'Importar Arquivo Produtos',
                  },
                },
              ],
            },
            {
              name: 'Visualizar Webservice Produtos',
              id: 'view_webserviceprodutos',
              module: 'integracao',
              model: { id: 'webserviceprodutos', name: 'Webservice' },
            },
          ],
        },
        {
          id: 'paginas',
          name: 'Páginas',
          children: [
            {
              id: 'comocomprar',
              name: 'Como comprar',
              children: [
                {
                  name: 'Adicionar Como comprar',
                  id: 'add_comocomprar',
                  module: 'paginas',
                  model: { id: 'comocomprar', name: 'Como Comprar' },
                },
                {
                  name: 'Editar Como comprar',
                  id: 'change_comocomprar',
                  module: 'paginas',
                  model: { id: 'comocomprar', name: 'Como Comprar' },
                },
                {
                  name: 'Deletar Como comprar',
                  id: 'delete_comocomprar',
                  module: 'paginas',
                  model: { id: 'comocomprar', name: 'Como Comprar' },
                },
                {
                  name: 'Visualizar Como comprar',
                  id: 'view_comocomprar',
                  module: 'paginas',
                  model: { id: 'comocomprar', name: 'Como Comprar' },
                },
              ],
            },
            {
              id: 'curriculorecebido',
              name: 'Curr\u00edculo recebido',
              children: [
                {
                  name: 'Adicionar Curr\u00edculo recebido',
                  id: 'add_curriculorecebido',
                  module: 'paginas',
                  model: { id: 'curriculorecebido', name: 'Curr\u00edculo Recebido' },
                },
                {
                  name: 'Editar Curr\u00edculo recebido',
                  id: 'change_curriculorecebido',
                  module: 'paginas',
                  model: { id: 'curriculorecebido', name: 'Curr\u00edculo Recebido' },
                },
                {
                  name: 'Deletar Curr\u00edculo recebido',
                  id: 'delete_curriculorecebido',
                  module: 'paginas',
                  model: { id: 'curriculorecebido', name: 'Curr\u00edculo Recebido' },
                },
                {
                  name: 'Visualizar Curr\u00edculo recebido',
                  id: 'view_curriculorecebido',
                  module: 'paginas',
                  model: { id: 'curriculorecebido', name: 'Curr\u00edculo Recebido' },
                },
              ],
            },
            {
              id: 'faq',
              name: 'FAQ',
              children: [
                {
                  name: 'Adicionar FAQ',
                  id: 'add_faq',
                  module: 'paginas',
                  model: { id: 'faq', name: 'Faq' },
                },
                {
                  name: 'Editar FAQ',
                  id: 'change_faq',
                  module: 'paginas',
                  model: { id: 'faq', name: 'Faq' },
                },
                {
                  name: 'Deletar FAQ',
                  id: 'delete_faq',
                  module: 'paginas',
                  model: { id: 'faq', name: 'Faq' },
                },
                {
                  name: 'Visualizar FAQ',
                  id: 'view_faq',
                  module: 'paginas',
                  model: { id: 'faq', name: 'Faq' },
                },
              ],
            },
            {
              id: 'linksrodape',
              name: 'Link externo',
              children: [
                {
                  name: 'Adicionar Link externo',
                  id: 'add_linksrodape',
                  module: 'paginas',
                  model: { id: 'linksrodape', name: 'Link Externo' },
                },
                {
                  name: 'Editar Link externo',
                  id: 'change_linksrodape',
                  module: 'paginas',
                  model: { id: 'linksrodape', name: 'Link Externo' },
                },
                {
                  name: 'Deletar Link externo',
                  id: 'delete_linksrodape',
                  module: 'paginas',
                  model: { id: 'linksrodape', name: 'Link Externo' },
                },
                {
                  name: 'Visualizar Link externo',
                  id: 'view_linksrodape',
                  module: 'paginas',
                  model: { id: 'linksrodape', name: 'Link Externo' },
                },
              ],
            },
            {
              id: 'outraspaginas',
              name: 'Outras p\u00e1ginas',
              children: [
                {
                  name: 'Adicionar Outras p\u00e1ginas',
                  id: 'add_outraspaginas',
                  module: 'paginas',
                  model: { id: 'outraspaginas', name: 'Outras P\u00e1ginas' },
                },
                {
                  name: 'Editar Outras p\u00e1ginas',
                  id: 'change_outraspaginas',
                  module: 'paginas',
                  model: { id: 'outraspaginas', name: 'Outras P\u00e1ginas' },
                },
                {
                  name: 'Deletar Outras p\u00e1ginas',
                  id: 'delete_outraspaginas',
                  module: 'paginas',
                  model: { id: 'outraspaginas', name: 'Outras P\u00e1ginas' },
                },
                {
                  name: 'Visualizar Outras p\u00e1ginas',
                  id: 'view_outraspaginas',
                  module: 'paginas',
                  model: { id: 'outraspaginas', name: 'Outras P\u00e1ginas' },
                },
              ],
            },
            {
              id: 'parceiro',
              name: 'Parceiro',
              children: [
                {
                  name: 'Adicionar Parceiro',
                  id: 'add_parceiro',
                  module: 'paginas',
                  model: { id: 'parceiro', name: 'Parceiro' },
                },
                {
                  name: 'Editar Parceiro',
                  id: 'change_parceiro',
                  module: 'paginas',
                  model: { id: 'parceiro', name: 'Parceiro' },
                },
                {
                  name: 'Deletar Parceiro',
                  id: 'delete_parceiro',
                  module: 'paginas',
                  model: { id: 'parceiro', name: 'Parceiro' },
                },
                {
                  name: 'Visualizar Parceiro',
                  id: 'view_parceiro',
                  module: 'paginas',
                  model: { id: 'parceiro', name: 'Parceiro' },
                },
              ],
            },
            {
              id: 'politicasdeprivacidade',
              name: 'Politica de privacidade',
              children: [
                {
                  name: 'Adicionar Politica de privacidade',
                  id: 'add_politicasdeprivacidade',
                  module: 'paginas',
                  model: {
                    id: 'politicasdeprivacidade',
                    name: 'Politica De Privacidade',
                  },
                },
                {
                  name: 'Visualizar Politica de privacidade',
                  id: 'view_politicasdeprivacidade',
                  module: 'paginas',
                  model: {
                    id: 'politicasdeprivacidade',
                    name: 'Politica De Privacidade',
                  },
                },
              ],
            },
            {
              id: 'sobre',
              name: 'Sobre',
              children: [
                {
                  name: 'Adicionar sobre',
                  id: 'add_sobre',
                  module: 'paginas',
                  model: { id: 'sobre', name: 'Sobre' },
                },
                {
                  name: 'Editar sobre',
                  id: 'change_sobre',
                  module: 'paginas',
                  model: { id: 'sobre', name: 'Sobre' },
                },
                {
                  name: 'Deletar sobre',
                  id: 'delete_sobre',
                  module: 'paginas',
                  model: { id: 'sobre', name: 'Sobre' },
                },
                {
                  name: 'Visualizar sobre',
                  id: 'view_sobre',
                  module: 'paginas',
                  model: { id: 'sobre', name: 'Sobre' },
                },
              ],
            },
            {
              id: 'termosdeuso',
              name: 'Termos de uso',
              children: [
                {
                  name: 'Adicionar Termos de uso',
                  id: 'add_termosdeuso',
                  module: 'paginas',
                  model: { id: 'termosdeuso', name: 'Termos De Uso' },
                },
                {
                  name: 'Editar Termos de uso',
                  id: 'change_termosdeuso',
                  module: 'paginas',
                  model: { id: 'termosdeuso', name: 'Termos De Uso' },
                },
                {
                  name: 'Deletar Termos de uso',
                  id: 'delete_termosdeuso',
                  module: 'paginas',
                  model: { id: 'termosdeuso', name: 'Termos De Uso' },
                },
                {
                  name: 'Visualizar Termos de uso',
                  id: 'view_termosdeuso',
                  module: 'paginas',
                  model: { id: 'termosdeuso', name: 'Termos De Uso' },
                },
              ],
            },
            {
              id: 'trocadevolucao',
              name: 'Troca e Devolução',
              children: [
                {
                  name: 'Adicionar Troca e devolu\u00e7\u00e3o',
                  id: 'add_trocadevolucao',
                  module: 'paginas',
                  model: {
                    id: 'trocadevolucao',
                    name: 'Troca E Devolu\u00e7\u00e3o',
                  },
                },
                {
                  name: 'Editar Troca e devolu\u00e7\u00e3o',
                  id: 'change_trocadevolucao',
                  module: 'paginas',
                  model: {
                    id: 'trocadevolucao',
                    name: 'Troca E Devolu\u00e7\u00e3o',
                  },
                },
                {
                  name: 'Deletar Troca e devolu\u00e7\u00e3o',
                  id: 'delete_trocadevolucao',
                  module: 'paginas',
                  model: {
                    id: 'trocadevolucao',
                    name: 'Troca E Devolu\u00e7\u00e3o',
                  },
                },
                {
                  name: 'Visualizar Troca e devolu\u00e7\u00e3o',
                  id: 'view_trocadevolucao',
                  module: 'paginas',
                  model: {
                    id: 'trocadevolucao',
                    name: 'Troca E Devolu\u00e7\u00e3o',
                  },
                },
              ],
            },
          ],
        },
        {
          id: 'produtosm',
          name: 'Produtos',
          children: [
            {
              id: 'carrosselhome',
              name: 'Carroussel Home',
              children: [
                {
                  name: 'Adicionar Carroussel Home',
                  id: 'add_carrosselhome',
                  module: 'produtos',
                  model: { id: 'carrosselhome', name: 'Carrossel Home' },
                },
                {
                  name: 'Editar Carroussel Home',
                  id: 'change_carrosselhome',
                  module: 'produtos',
                  model: { id: 'carrosselhome', name: 'Carrossel Home' },
                },
                {
                  name: 'Deletar Carroussel Home',
                  id: 'delete_carrosselhome',
                  module: 'produtos',
                  model: { id: 'carrosselhome', name: 'Carrossel Home' },
                },
                {
                  name: 'Visualizar Carroussel Home',
                  id: 'view_carrosselhome',
                  module: 'produtos',
                  model: { id: 'carrosselhome', name: 'Carrossel Home' },
                },
              ],
            },
            {
              id: 'combo',
              name: 'Combo',
              children: [
                {
                  name: 'Adicionar Combo',
                  id: 'add_combo',
                  module: 'produtos',
                  model: { id: 'combo', name: 'Combo' },
                },
                {
                  name: 'Editar Combo',
                  id: 'change_combo',
                  module: 'produtos',
                  model: { id: 'combo', name: 'Combo' },
                },
                {
                  name: 'Deletar Combo',
                  id: 'delete_combo',
                  module: 'produtos',
                  model: { id: 'combo', name: 'Combo' },
                },
                {
                  name: 'Visualizar Combo',
                  id: 'view_combo',
                  module: 'produtos',
                  model: { id: 'combo', name: 'Combo' },
                },
              ],
            },
            {
              id: 'departamento',
              name: 'Departamento',
              children: [
                {
                  name: 'Adicionar departamento',
                  id: 'add_departamento',
                  module: 'produtos',
                  model: { id: 'departamento', name: 'Departamento' },
                },
                {
                  name: 'Editar departamento',
                  id: 'change_departamento',
                  module: 'produtos',
                  model: { id: 'departamento', name: 'Departamento' },
                },
                {
                  name: 'Deletar departamento',
                  id: 'delete_departamento',
                  module: 'produtos',
                  model: { id: 'departamento', name: 'Departamento' },
                },
                {
                  name: 'Visualizar departamento',
                  id: 'view_departamento',
                  module: 'produtos',
                  model: { id: 'departamento', name: 'Departamento' },
                },
              ],
            },
            {
              id: 'encarte',
              name: 'Encarte',
              children: [
                {
                  name: 'Adicionar Encarte',
                  id: 'add_encarte',
                  module: 'produtos',
                  model: { id: 'encarte', name: 'Encarte' },
                },
                {
                  name: 'Editar Encarte',
                  id: 'change_encarte',
                  module: 'produtos',
                  model: { id: 'encarte', name: 'Encarte' },
                },
                {
                  name: 'Deletar Encarte',
                  id: 'delete_encarte',
                  module: 'produtos',
                  model: { id: 'encarte', name: 'Encarte' },
                },
                {
                  name: 'Visualizar Encarte',
                  id: 'view_encarte',
                  module: 'produtos',
                  model: { id: 'encarte', name: 'Encarte' },
                },
              ],
            },
            {
              id: 'fracao',
              name: 'Fra\u00e7\u00e3o',
              children: [
                {
                  name: 'Adicionar Fra\u00e7\u00e3o',
                  id: 'add_fracao',
                  module: 'produtos',
                  model: { id: 'fracao', name: 'Fra\u00e7\u00e3o' },
                },
                {
                  name: 'Editar Fra\u00e7\u00e3o',
                  id: 'change_fracao',
                  module: 'produtos',
                  model: { id: 'fracao', name: 'Fra\u00e7\u00e3o' },
                },
                {
                  name: 'Deletar Fra\u00e7\u00e3o',
                  id: 'delete_fracao',
                  module: 'produtos',
                  model: { id: 'fracao', name: 'Fra\u00e7\u00e3o' },
                },
                {
                  name: 'Visualizar Fra\u00e7\u00e3o',
                  id: 'view_fracao',
                  module: 'produtos',
                  model: { id: 'fracao', name: 'Fra\u00e7\u00e3o' },
                },
              ],
            },
            {
              name: 'Visualizar Hist\u00f3rico do produto',
              id: 'view_historicoproduto',
              module: 'produtos',
              model: {
                id: 'historicoproduto',
                name: 'Hist\u00f3rico Do Produto',
              },
            },
            {
              id: 'imagemencarte',
              name: 'Imagem do Encarte',
              children: [
                {
                  name: 'Adicionar Imagem do Encarte',
                  id: 'add_imagemencarte',
                  module: 'produtos',
                  model: { id: 'imagemencarte', name: 'Imagem Do Encarte' },
                },
                {
                  name: 'Editar Imagem do Encarte',
                  id: 'change_imagemencarte',
                  module: 'produtos',
                  model: { id: 'imagemencarte', name: 'Imagem Do Encarte' },
                },
                {
                  name: 'Deletar Imagem do Encarte',
                  id: 'delete_imagemencarte',
                  module: 'produtos',
                  model: { id: 'imagemencarte', name: 'Imagem Do Encarte' },
                },
                {
                  name: 'Visualizar Imagem do Encarte',
                  id: 'view_imagemencarte',
                  module: 'produtos',
                  model: { id: 'imagemencarte', name: 'Imagem Do Encarte' },
                },
              ],
            },
            {
              id: 'itemcarroussel',
              name: 'Item carroussel',
              children: [
                {
                  name: 'Adicionar Item carroussel',
                  id: 'add_itemcarroussel',
                  module: 'produtos',
                  model: { id: 'itemcarroussel', name: 'Item Carrossel' },
                },
                {
                  name: 'Editar Item carroussel',
                  id: 'change_itemcarroussel',
                  module: 'produtos',
                  model: { id: 'itemcarroussel', name: 'Item Carrossel' },
                },
                {
                  name: 'Deletar Item carroussel',
                  id: 'delete_itemcarroussel',
                  module: 'produtos',
                  model: { id: 'itemcarroussel', name: 'Item Carrossel' },
                },
                {
                  name: 'Visualizar Item carroussel',
                  id: 'view_itemcarroussel',
                  module: 'produtos',
                  model: { id: 'itemcarroussel', name: 'Item Carrossel' },
                },
              ],
            },
            {
              id: 'itemcombo',
              name: 'Item do combo',
              children: [
                {
                  name: 'Adicionar Item do combo',
                  id: 'add_itemcombo',
                  module: 'produtos',
                  model: { id: 'itemcombo', name: 'Item Do Combo' },
                },
                {
                  name: 'Editar Item do combo',
                  id: 'change_itemcombo',
                  module: 'produtos',
                  model: { id: 'itemcombo', name: 'Item Do Combo' },
                },
                {
                  name: 'Deletar Item do combo',
                  id: 'delete_itemcombo',
                  module: 'produtos',
                  model: { id: 'itemcombo', name: 'Item Do Combo' },
                },
                {
                  name: 'Visualizar Item do combo',
                  id: 'view_itemcombo',
                  module: 'produtos',
                  model: { id: 'itemcombo', name: 'Item Do Combo' },
                },
              ],
            },
            {
              id: 'itemselecao',
              name: 'Item sele\u00e7\u00e3o',
              children: [
                {
                  name: 'Adicionar Item sele\u00e7\u00e3o',
                  id: 'add_itemselecao',
                  module: 'produtos',
                  model: { id: 'itemselecao', name: 'Item Da Sele\u00e7\u00e3o' },
                },
                {
                  name: 'Editar Item sele\u00e7\u00e3o',
                  id: 'change_itemselecao',
                  module: 'produtos',
                  model: { id: 'itemselecao', name: 'Item Da Sele\u00e7\u00e3o' },
                },
                {
                  name: 'Deletar Item sele\u00e7\u00e3o',
                  id: 'delete_itemselecao',
                  module: 'produtos',
                  model: { id: 'itemselecao', name: 'Item Da Sele\u00e7\u00e3o' },
                },
                {
                  name: 'Visualizar Item sele\u00e7\u00e3o',
                  id: 'view_itemselecao',
                  module: 'produtos',
                  model: { id: 'itemselecao', name: 'Item Da Sele\u00e7\u00e3o' },
                },
              ],
            },
            {
              id: 'marca',
              name: 'Marca',
              children: [
                {
                  name: 'Adicionar marca',
                  id: 'add_marca',
                  module: 'produtos',
                  model: { id: 'marca', name: 'Marca' },
                },
                {
                  name: 'Editar marca',
                  id: 'change_marca',
                  module: 'produtos',
                  model: { id: 'marca', name: 'Marca' },
                },
                {
                  name: 'Deletar marca',
                  id: 'delete_marca',
                  module: 'produtos',
                  model: { id: 'marca', name: 'Marca' },
                },
                {
                  name: 'Visualizar marca',
                  id: 'view_marca',
                  module: 'produtos',
                  model: { id: 'marca', name: 'Marca' },
                },
              ],
            },
            {
              id: 'medida',
              name: 'Medida',
              children: [
                {
                  name: 'Adicionar medida',
                  id: 'add_medida',
                  module: 'produtos',
                  model: { id: 'medida', name: 'Medida' },
                },
                {
                  name: 'Editar medida',
                  id: 'change_medida',
                  module: 'produtos',
                  model: { id: 'medida', name: 'Medida' },
                },
                {
                  name: 'Deletar medida',
                  id: 'delete_medida',
                  module: 'produtos',
                  model: { id: 'medida', name: 'Medida' },
                },
                {
                  name: 'Visualizar medida',
                  id: 'view_medida',
                  module: 'produtos',
                  model: { id: 'medida', name: 'Medida' },
                },
              ],
            },
            {
              id: 'modelo',
              name: 'Modelo',
              children: [
                {
                  name: 'Adicionar modelo',
                  id: 'add_modelo',
                  module: 'produtos',
                  model: { id: 'modelo', name: 'Modelo' },
                },
                {
                  name: 'Editar modelo',
                  id: 'change_modelo',
                  module: 'produtos',
                  model: { id: 'modelo', name: 'Modelo' },
                },
                {
                  name: 'Deletar modelo',
                  id: 'delete_modelo',
                  module: 'produtos',
                  model: { id: 'modelo', name: 'Modelo' },
                },
                {
                  name: 'Visualizar modelo',
                  id: 'view_modelo',
                  module: 'produtos',
                  model: { id: 'modelo', name: 'Modelo' },
                },
              ],
            },
            {
              id: 'produto',
              name: 'Produto',
              children: [
                {
                  name: 'Adicionar produto',
                  id: 'add_produto',
                  module: 'produtos',
                  model: { id: 'produto', name: 'Produto' },
                },
                {
                  name: 'Editar produto',
                  id: 'change_produto',
                  module: 'produtos',
                  model: { id: 'produto', name: 'Produto' },
                },
                {
                  name: 'Deletar produto',
                  id: 'delete_produto',
                  module: 'produtos',
                  model: { id: 'produto', name: 'Produto' },
                },
                {
                  name: 'Visualizar produto',
                  id: 'view_produto',
                  module: 'produtos',
                  model: { id: 'produto', name: 'Produto' },
                },
              ],
            },
            {
              id: 'produtoreceita',
              name: 'Produto receita',
              children: [
                {
                  name: 'Adicionar Produto receita',
                  id: 'add_produtoreceita',
                  module: 'produtos',
                  model: { id: 'produtoreceita', name: 'Produto Receita' },
                },
                {
                  name: 'Editar Produto receita',
                  id: 'change_produtoreceita',
                  module: 'produtos',
                  model: { id: 'produtoreceita', name: 'Produto Receita' },
                },
                {
                  name: 'Deletar Produto receita',
                  id: 'delete_produtoreceita',
                  module: 'produtos',
                  model: { id: 'produtoreceita', name: 'Produto Receita' },
                },
                {
                  name: 'Visualizar Produto receita',
                  id: 'view_produtoreceita',
                  module: 'produtos',
                  model: { id: 'produtoreceita', name: 'Produto Receita' },
                },
              ],
            },
            {
              id: 'receita',
              name: 'Receita',
              children: [
                {
                  name: 'Adicionar receita',
                  id: 'add_receita',
                  module: 'produtos',
                  model: { id: 'receita', name: 'Receita' },
                },
                {
                  name: 'Editar receita',
                  id: 'change_receita',
                  module: 'produtos',
                  model: { id: 'receita', name: 'Receita' },
                },
                {
                  name: 'Deletar receita',
                  id: 'delete_receita',
                  module: 'produtos',
                  model: { id: 'receita', name: 'Receita' },
                },
                {
                  name: 'Visualizar receita',
                  id: 'view_receita',
                  module: 'produtos',
                  model: { id: 'receita', name: 'Receita' },
                },
              ],
            },
            {
              id: 'selecaoprodutos',
              name: 'Sele\u00e7\u00e3o Produto',
              children: [
                {
                  name: 'Adicionar Sele\u00e7\u00e3o Produto',
                  id: 'add_selecaoprodutos',
                  module: 'produtos',
                  model: { id: 'selecaoprodutos', name: 'Sele\u00e7\u00e3o Produto' },
                },
                {
                  name: 'Editar Sele\u00e7\u00e3o Produto',
                  id: 'change_selecaoprodutos',
                  module: 'produtos',
                  model: { id: 'selecaoprodutos', name: 'Sele\u00e7\u00e3o Produto' },
                },
                {
                  name: 'Deletar Sele\u00e7\u00e3o Produto',
                  id: 'delete_selecaoprodutos',
                  module: 'produtos',
                  model: { id: 'selecaoprodutos', name: 'Sele\u00e7\u00e3o Produto' },
                },
                {
                  name: 'Visualizar Sele\u00e7\u00e3o Produto',
                  id: 'view_selecaoprodutos',
                  module: 'produtos',
                  model: { id: 'selecaoprodutos', name: 'Sele\u00e7\u00e3o Produto' },
                },
              ],
            },
            {
              name: 'Visualizar Sugest\u00e3o de produto',
              id: 'view_sugestaoproduto',
              module: 'produtos',
              model: { id: 'sugestaoproduto', name: 'Sugest\u00e3o De Produto' },
            },
            {
              name: 'Visualizar Termo n\u00e3o encontrado',
              id: 'view_termonaoencontrado',
              module: 'produtos',
              model: {
                id: 'termonaoencontrado',
                name: 'Termo N\u00e3o Encontrado',
              },
            },
            {
              id: 'variacao',
              name: 'Varia\u00e7\u00e3o',
              children: [
                {
                  name: 'Adicionar Varia\u00e7\u00e3o',
                  id: 'add_variacao',
                  module: 'produtos',
                  model: { id: 'variacao', name: 'Varia\u00e7\u00e3o' },
                },
                {
                  name: 'Editar Varia\u00e7\u00e3o',
                  id: 'change_variacao',
                  module: 'produtos',
                  model: { id: 'variacao', name: 'Varia\u00e7\u00e3o' },
                },
                {
                  name: 'Deletar Varia\u00e7\u00e3o',
                  id: 'delete_variacao',
                  module: 'produtos',
                  model: { id: 'variacao', name: 'Varia\u00e7\u00e3o' },
                },
                {
                  name: 'Visualizar Varia\u00e7\u00e3o',
                  id: 'view_variacao',
                  module: 'produtos',
                  model: { id: 'variacao', name: 'Varia\u00e7\u00e3o' },
                },
              ],
            },
            {
              name: 'Visualizar relatórios',
              id: 'view_relatorios',
              module: 'relatorios',
              model: { id: 'relatorios', name: 'Relatorios' },
            },
          ],
        },
      ]
    },
    allPages() {
      return this.items.reduce((accumulator, current) => {
        if (current.children) {
          return [...accumulator, ...current.children.map((obj) => obj.id)]
        } else {
          return [...accumulator, current.id]
        }
      }, [])
    },
    allPagesEcommerce() {
      return this.itemsEcommerce.reduce((accumulator, current) => {
        if (current.children) {
          return [...accumulator, ...current.children.map((obj) => obj.id)]
        } else {
          return [...accumulator, current.id]
        }
      }, [])
    },
    submitValidations() {
      if (!this.pages || !this.pages.length) {
        return true
      }

      if (!this.user.multi_user && (!this.user.units || !this.user.units.length)) {
        return true
      }

      return false
    },
  },
  watch: {
    'user.accessSensitiveData': {
      handler(newVal) {
        if (!newVal) {
          this.user.downloadSensitiveDataPermission = false
        }
      },
    },
  },
  async created() {
    await this.getData()
  },
  methods: {
    clearAllUnits() {
      this.user.units = []
    },

    selectAllAreas() {
      if (this.hasCrmPermission) this.pages = this.allPages
      if (this.hasEcommercePermission) this.ecommercePermissions = this.allPagesEcommerce
    },
    clearAllAreas() {
      this.pages = []
      this.ecommercePermissions = []
      this.user.ecommerce = []
      this.user.profiles = []
    },
    async getData() {
      this.loadingDialog = true
      try {
        const {
          data: { getPlatformUser },
        } = await this.$apollo.query({
          query: QUERY_GET_USER,
          fetchPolicy: 'no-cache',
          variables: {
            email: this.$route.params.email,
          },
        })
        const permissions = getPlatformUser.permissions
        this.user = {
          units: [],
          ...getPlatformUser,
        }
        if (!this.user.permissions.crm.permissions) this.$set(this.user, 'pages', [])

        this.ecommercePermissions = permissions?.ecommerce?.permissions
        this.pages = permissions?.crm?.permissions
        this.hasCrmPermission = !!permissions && !!permissions.crm && !!permissions.crm.permissions.length
        this.hasEcommercePermission = this.$getLoggedUser().has_ecommerce
        if (permissions.crm?.roles.find((role) => role === 'admin')) {
          this.user.admin = true
        }
        this.user.ecommerce = permissions?.ecommerce?.roles
        this.user.profiles = this.generateEditProfiles(permissions?.crm?.roles)
        this.storeAccess.ecommerce = permissions?.storeAccess?.ecommerce ?? false
        this.storeAccess.crm = permissions?.storeAccess?.crm ?? false
        this.user.accessSensitiveData = permissions?.crm?.accessSensitiveData
        this.user.accessSensitiveDataEmployees = permissions?.crm?.accessSensitiveDataEmployees
        this.user.downloadSensitiveDataPermission = permissions?.crm?.downloadSensitiveDataPermission
        this.user.units = permissions?.stores
          .filter((store) => store.accountId === this.$getLoggedUser().id_account)
          .map((store) => {
            return store.id
          })
        this.userBackup = cloneDeep(this.user)
        this.otherStoresAccounts = permissions?.stores.filter((store) => store.accountId !== this.$getLoggedUser().id_account)
      } catch (error) {
        this.$snackbar({ message: 'Usuário não encontrado', snackbarColor: 'error' })
      } finally {
        this.loadingDialog = false
      }
    },
    disabledByFranchise(page) {
      if (this.isFranchisee) {
        const permissionKey = Object.keys(this.franchiseePermissionsRoutes).find((key) => {
          return this.franchiseePermissionsRoutes[key] === page
        })
        return this.franchiseePermissions[permissionKey] === false
      } else {
        return false
      }
    },
    generateEditProfiles(profilesPages) {
      if (profilesPages) {
        let profiles = profilesPages.reduce((profilesArray, current) => {
          const filterProfile = this.profilesPages.filter((item) => item.value === current)

          if (filterProfile.length) profilesArray.push(...filterProfile)

          return profilesArray
        }, [])

        return profiles
      }
    },
    async submit() {
      let sideMenuError = true
      this.loading = true
      this.allPages.forEach((page) => {
        if (page !== 'usuarios/editar' && page !== 'usuarios/permissionamento' && this.pages.includes(page)) {
          sideMenuError = false
        }
      })

      if (sideMenuError) {
        this.$alert({
          title: 'Aviso',
          message: 'Não é possível salvar um usuário com acesso apenas às telas de Usuários',
        })
        this.loading = false

        return
      }

      if (this.user.multi_user) {
        this.clearAllUnits()
      }

      const storesToPayload = [
        ...this.user.units.map((unit) => {
          return {
            id: unit,
            accountId: this.$getLoggedUser().id_account,
          }
        }),
        ...this.otherStoresAccounts.map(({ id, accountId, club, ecommerce }) => ({ id, accountId, club, ecommerce })),
      ]

      const payload = {
        name: this.user.name,
        email: this.user.email,
        permissionValue: {
          storeAccess: {
            ecommerce: this.storeAccess.ecommerce,
            crm: this.storeAccess.crm,
          },
          stores: storesToPayload,
          crm: {
            permissions: [...this.pages],
            roles: [...this.user.profiles.map((profile) => profile.value)],
            accessSensitiveData: this.user.accessSensitiveData,
            accessSensitiveDataEmployees: this.user.accessSensitiveDataEmployees,
            downloadSensitiveDataPermission: this.user.downloadSensitiveDataPermission,
          },
          ecommerce: {
            permissions: [...this.ecommercePermissions],
            roles: [...this.user.ecommerce],
          },
        },
      }

      if (this.user.admin) {
        payload.permissionValue.crm.roles.push('admin')
      }

      try {
        this.$confirm({
          title: 'Salvar permissionamento',
          message: 'Tem certeza que deseja salvar as permissões?',
          confirmText: 'Salvar',
          confirmColor: 'primary',
          cancelText: 'Cancelar',
        })
          .then(async () => {
            await this.$apollo.mutate({
              mutation: MUTATION_UPDATE_USER,
              variables: payload,
            })
            this.userBackup = cloneDeep(this.user)
            this.$snackbar({ message: 'Salvo com sucesso!', snackbarColor: '#51a558' })
            this.$router.push('/usuarios').catch(() => {})
          })
          .catch(() => {})
      } catch (error) {
        this.$snackbar({ message: 'Erro ao salvar usuário!', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    changeProfileSelection(newProfiles) {
      this.user.accessSensitiveData = newProfiles.some((profile) => profile.has_pii_access)
      this.user.downloadSensitiveDataPermission = newProfiles.some((profile) => profile.has_pii_download_data_permission)
      this.filteredUserPages = []
      newProfiles.map((profile) => {
        this.filteredUserPages.push(...profile.pages)
      })
    },
    back() {
      this.$router.push('/usuarios').catch(() => {})
    },
  },

  beforeRouteLeave(to, from, next) {
    // TODO login tratativa
    if (isEqual(this.userBackup, this.user) || to.path === '/login') {
      next()
    } else {
      this.$confirm({
        title: 'Ops, parece que você não salvou suas alterações',
        message: 'Tem certeza que deseja sair sem salvar as alterações? Se continuar, todas as alterações serão desconsideradas.',
        confirmText: 'Continuar',
        confirmColor: 'primary',
        cancelText: 'Cancelar',
      }).then(
        () => {
          next()
        },
        () => {}
      )
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-treeview-node__toggle,
::v-deep .v-treeview-node__checkbox {
  &:focus {
    outline: none;
  }
}
.permissionamento {
  .treeview-container {
    overflow-y: scroll;
    height: 379px;
    overflow-x: hidden;
    ::v-deep .v-treeview-node {
      &__root {
        width: fit-content;
      }
      &--disabled {
        pointer-events: all;
      }
    }
  }
  .username {
    margin-top: 20px;
    opacity: 0.8;
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.22;
    letter-spacing: 0.29px;

    b {
      color: $marca-1;
    }
  }

  .units {
    .units-title {
      opacity: 0.8;
      font-size: 18px;
      font-weight: bold;
      line-height: 1.22;
      letter-spacing: 0.29px;
    }
    .units-subtitle {
      opacity: 0.8;
      font-size: 15px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.2;
      letter-spacing: 0.24px;
    }

    .unit-chip {
      border-radius: 17px;
      text-align: center;
      padding: 4px 15px;
      border: 1px solid rgba(0, 0, 0, 0.2);
      transition: all linear 100ms;
    }
  }

  .areas {
    .areas-title {
      opacity: 0.8;
      font-size: 18px;
      font-weight: bold;
      line-height: 1.22;
      letter-spacing: 0.29px;
    }
    .areas-subtitle {
      opacity: 0.8;
      font-size: 15px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.2;
      letter-spacing: 0.24px;
    }
  }
}

.header-treeview {
  display: flex;
  justify-content: space-between;
  place-items: baseline;
}

.mf-toggle-container {
  width: 280px;
  .header-treeview {
    ::v-deep .v-input--selection-controls {
      display: flex;
      margin: 0 !important;
      justify-content: space-between;
      padding: 0 !important;
      place-items: baseline;
    }
  }
}
</style>
